import { Link } from '@tanstack/react-router'
import type { RecurringInvoiceResponseDto } from '@trellis/api'
import { Button } from '@trellis/roots/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@trellis/roots/dropdown-menu'
import { Edit, MoreHorizontal, Pause, Play, X } from 'lucide-react'

interface RecurringInvoiceRowActionsMenuProps {
  recurringInvoice: RecurringInvoiceResponseDto
  isTriggerPending: boolean
  isStatusPending: boolean
  onTrigger: (id: string) => void
  onPause: (id: string) => void
  onActivate: (id: string) => void
  onCancel: () => void
}

export function RecurringInvoiceRowActionsMenu({
  recurringInvoice,
  isTriggerPending,
  isStatusPending,
  onTrigger,
  onPause,
  onActivate,
  onCancel,
}: RecurringInvoiceRowActionsMenuProps) {
  const isActive = recurringInvoice.status === 'ACTIVE'
  const isPaused = recurringInvoice.status === 'PAUSED'
  const isTerminal =
    recurringInvoice.status === 'CANCELLED' || recurringInvoice.status === 'FINISHED'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Open actions for ${recurringInvoice.rinvLabel}`}
          onClick={(clickEvent) => clickEvent.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => onTrigger(recurringInvoice.id)}
          disabled={isTriggerPending || isTerminal}
        >
          <Play className="mr-2 h-4 w-4" />
          Trigger run
        </DropdownMenuItem>

        {isActive && (
          <DropdownMenuItem
            onClick={() => onPause(recurringInvoice.id)}
            disabled={isStatusPending}
          >
            <Pause className="mr-2 h-4 w-4" />
            Pause
          </DropdownMenuItem>
        )}

        {isPaused && (
          <DropdownMenuItem
            onClick={() => onActivate(recurringInvoice.id)}
            disabled={isStatusPending}
          >
            <Play className="mr-2 h-4 w-4" />
            Activate
          </DropdownMenuItem>
        )}

        {!isTerminal && (
          <>
            <DropdownMenuItem asChild>
              <Link
                to="/invoices/recurring-invoices/$id/edit"
                params={{ id: recurringInvoice.id }}
              >
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive-text focus:text-destructive-text"
              onClick={onCancel}
              disabled={isStatusPending}
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
